/**
 * /my-tastings — the read-only "what events am I on?" page.
 *
 * The default landing for lead_host + host roles. Also accessible to
 * admin/dispatcher/sales so they can see their own crew assignments.
 * Sources data from GET /api/me/tastings which joins by the user's
 * email to the Contact record on file.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bus,
  Calendar,
  Clock,
  MapPin,
  Building2,
  Phone,
  Star,
  QrCode,
} from "lucide-react";
import { fetchMyTastings } from "../lib/api";
import { FeedbackQRDialog } from "../components/FeedbackQRDialog";

const ROLE_CHIP = {
  driver:    { label: "Driver",    cls: "bg-amber-500/15 text-amber-200 border-amber-500/30" },
  host:      { label: "Host",      cls: "bg-cyan-500/15 text-cyan-200 border-cyan-500/30" },
  lead_host: { label: "Lead Host", cls: "bg-violet-500/15 text-violet-200 border-violet-500/30" },
};

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function buildMapsUrl(stop) {
  if (typeof stop.latitude === "number" && typeof stop.longitude === "number") {
    return `https://www.google.com/maps/dir/?api=1&destination=${stop.latitude},${stop.longitude}`;
  }
  if (stop.address) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(stop.address + " " + (stop.dispensary_city || ""))}`;
  }
  return null;
}

/**
 * /my-tastings — bus tasting events the current user is on the crew for.
 * Default landing for host / lead_host roles, also visible to admin /
 * dispatcher / sales who can be assigned as crew.
 */
export default function MyTastings() {
  const [items, setItems] = useState(null);
  const [qrTasting, setQrTasting] = useState(null);

  useEffect(() => {
    fetchMyTastings()
      .then(setItems)
      .catch(() => setItems([]));
  }, []);

  const upcoming = (items || []).filter((t) => t.status === "scheduled");
  const past = (items || []).filter((t) => t.status === "completed");

  return (
    <div className="max-w-3xl mx-auto space-y-8 fade-up">
      <section data-testid="my-tastings-upcoming">
        <div className="flex items-center gap-2 mb-1">
          <Bus className="w-5 h-5 text-emerald-400" />
          <h2 className="text-lg font-semibold">Upcoming events</h2>
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          These are the bus events you're scheduled to attend.
        </p>
        {items === null ? (
          <div className="text-sm text-zinc-500">Loading…</div>
        ) : upcoming.length === 0 ? (
          <div
            data-testid="my-tastings-empty"
            className="panel p-6 text-center text-sm text-zinc-500"
          >
            No upcoming events scheduled. The dispatcher will let you know
            when you're assigned to one.
          </div>
        ) : (
          <ul className="space-y-3">
            {upcoming.map((t) => {
              const meta = ROLE_CHIP[t.my_role] || ROLE_CHIP.host;
              const mapsUrl = buildMapsUrl(t);
              return (
                <li
                  key={t.id}
                  data-testid={`my-tasting-${t.id}`}
                  className="panel p-4 space-y-3 border border-emerald-500/20"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-zinc-500" />
                        <span className="font-semibold text-zinc-100">
                          {t.dispensary_name}
                        </span>
                      </div>
                      <div className="text-xs text-zinc-500 ml-6">
                        {t.dispensary_city || "—"}
                        {t.dispensary_county
                          ? ` · ${t.dispensary_county} County`
                          : ""}
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border ${meta.cls}`}
                    >
                      {t.my_role === "lead_host" && (
                        <Star className="w-2.5 h-2.5" />
                      )}
                      {meta.label}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-zinc-300 flex-wrap">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-emerald-400" />
                      {formatDate(t.scheduled_date)}
                    </span>
                    {t.scheduled_time && (
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-emerald-400" />
                        {t.scheduled_time}
                      </span>
                    )}
                    {t.trip_id && t.trip_name && (
                      <Link
                        to={`/trips/${t.trip_id}`}
                        data-testid={`my-tasting-trip-link-${t.id}`}
                        className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded bg-violet-500/15 border border-violet-500/40 text-violet-200 hover:bg-violet-500/25 hover:text-violet-100"
                        title="Open full trip detail"
                      >
                        <Bus className="w-3 h-3" />
                        {t.trip_name}
                        {t.trip_day_number && t.trip_total_days && (
                          <span className="text-violet-300/80">
                            · Day {t.trip_day_number} of {t.trip_total_days}
                          </span>
                        )}
                      </Link>
                    )}
                  </div>

                  {t.dispatcher_notes && (
                    <div className="text-xs text-zinc-400 leading-relaxed border-l-2 border-zinc-800 pl-3">
                      {t.dispatcher_notes}
                    </div>
                  )}

                  <div className="flex items-center gap-2 flex-wrap">
                    {mapsUrl && (
                      <a
                        href={mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs border border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-200"
                      >
                        <MapPin className="w-3 h-3 text-emerald-400" />
                        Directions
                      </a>
                    )}
                    {t.phone && (
                      <a
                        href={`tel:${t.phone}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs border border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-200"
                      >
                        <Phone className="w-3 h-3 text-emerald-400" />
                        {t.phone}
                      </a>
                    )}
                    {/* Feedback QR — driver/host phone fallback for
                        when the picture frame fails. */}
                    <button
                      type="button"
                      data-testid={`my-tasting-qr-${t.id}`}
                      onClick={() => setQrTasting(t)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/15 text-emerald-200"
                      title="Show the feedback QR — for displaying on your phone if the picture frame fails"
                    >
                      <QrCode className="w-3 h-3" />
                      Feedback QR
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {past.length > 0 && (
        <section data-testid="my-tastings-past">
          <h2 className="text-lg font-semibold mb-1">Completed</h2>
          <p className="text-xs text-zinc-500 mb-4">
            Past events you attended.
          </p>
          <ul className="space-y-2">
            {past.map((t) => {
              const meta = ROLE_CHIP[t.my_role] || ROLE_CHIP.host;
              return (
                <li
                  key={t.id}
                  className="panel p-3 flex items-center justify-between gap-3 opacity-70"
                >
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-200 truncate">
                      {t.dispensary_name}
                    </div>
                    <div className="text-xs text-zinc-500">
                      {formatDate(t.scheduled_date)}
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border ${meta.cls}`}
                  >
                    {meta.label}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <FeedbackQRDialog
        open={!!qrTasting}
        onOpenChange={(o) => { if (!o) setQrTasting(null); }}
        tasting={qrTasting}
      />
    </div>
  );
}
